import { PartnerType } from 'legacy/lut/classes/models/PartnerType'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { TRANSLATION_KEYS } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/translations'
import { selectPartnerAccess } from 'legacy/lut/features/partner360/configuration'
import { FC, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { FormDropdown, FormInput, useFormField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { IMappedProductTreeItem, ProductTree } from 'partner360/components/filters'
import { parseKeyInitiativeFocusArea } from 'partner360/features/keyInitiativeEditor/lib/parseKeyInitiativeFocusArea'

import { SHOW_NEW_FOCUS_AREA_VIEW_SINCE_THIS_YEAR } from './configs'
import { IFocusArea } from './models'

interface IFocusAreaProps {
  initialValue?: string
  className?: string
  focusAreaValues: IFocusArea[]
  isDisabled?: boolean
  businessUnit?: string
  fYear: number
  programType: ProgramTypes
  formKey: string
  extendedItems?: IMappedProductTreeItem[]
}

export const FocusArea: FC<IFocusAreaProps> = ({
  initialValue = '',
  className,
  isDisabled,
  focusAreaValues,
  businessUnit,
  fYear,
  programType,
  formKey,
  extendedItems,
}) => {
  const { value, handleChange } = useFormField({
    defaultValue: initialValue,
    formKey,
    field: 'focusArea',
  })

  const { isDistributor } = useSelector(selectPartnerAccess)
  const selectedFocusAreaValues = useMemo(() => parseKeyInitiativeFocusArea(value), [value])

  const focusAreaCrud = useMemo(() => {
    return new Crud({
      fetcher: new Static(focusAreaValues),
      keyField: 'area',
      defaultPaging: {
        page: 0,
        pageSize: focusAreaValues.length,
      },
    })
  }, [focusAreaValues])

  const handleProductTreeChange = useCallback(
    (displayedIds: string[]) => {
      handleChange(displayedIds.join(','))
    },
    [handleChange]
  )

  if (fYear >= SHOW_NEW_FOCUS_AREA_VIEW_SINCE_THIS_YEAR) {
    return (
      <ProductTree
        partnerType={isDistributor ? PartnerType.Distributor : PartnerType.Commercial}
        fYear={fYear}
        className={className}
        values={selectedFocusAreaValues}
        label={
          <Translation
            text={TRANSLATION_KEYS.keyInitiativeEditor.businessUnitAndFocusFieldGroup.focusArea}
          />
        }
        onSelectChange={handleProductTreeChange}
        root={businessUnit || programType}
        extendedItems={extendedItems}
        programType={programType}
      />
    )
  }

  return focusAreaValues.length ? (
    <FormDropdown
      isStatic
      formKey={formKey}
      field="focusArea"
      defaultValue={initialValue}
      label={TRANSLATION_KEYS.keyInitiativeEditor.businessUnitAndFocusFieldGroup.focusArea}
      caption="area"
      classes={{ controlClasses: { root: className } }}
      source={focusAreaCrud}
      isDisabled={isDisabled}
    />
  ) : (
    <FormInput
      isDisabled={isDisabled}
      formKey={formKey}
      field="focusArea"
      defaultValue={initialValue}
      type="text"
      className={className}
      label={TRANSLATION_KEYS.keyInitiativeEditor.businessUnitAndFocusFieldGroup.focusArea}
    />
  )
}

FocusArea.displayName = 'FocusArea'
